import Insight from "../insightClass";
import React from "react";
import { insightCBAMMedia } from "../../../assets/assetReference";
import { ContactSubscribe } from "../../../components/Contact/Contact";
export const insight092 = new Insight(
  "23 January 2026",
  {
    pageTitle:
      "CBAM: Mitigation Strategies for Non-EU Offshore Wind Manufacturers",
    metaDescription:
      "WeBridge Insights, CBAM: Mitigation Strategies for Non-EU Offshore Wind Manufacturers",
    keyWords:
      "European Offshore Wind Market development, CBAM: Mitigation Strategies for Non-EU Offshore Wind Manufacturers",
  },
  {
    src: insightCBAMMedia[3],
    alt: "CBAM",
  },
  "CBAM: Mitigation Strategies for Non-EU Offshore Wind Manufacturers",
  "The new CBAM regulations raise difficult operational questions for offshore wind manufacturers exporting to the EU. But the earlier you move to mitigate the challenges, the sooner you can capitalise on the sizeable commercial opportunities.",
  `For offshore wind manufacturers who export to EU customers, the new CBAM regulations are forcing a strategic and operational rethink. Yes, the legal obligation to surrender CBAM certificates rests with the EU customer. But this economic burden will inevitably be pushed upstream.`,

  <div>
    <strong>WeBridge Insights</strong>
    <h1>CBAM: Mitigation Strategies for Non-EU Offshore Wind Manufacturers</h1>
    <h2>
      The new CBAM regulations raise difficult operational questions for
      offshore wind manufacturers exporting to the EU. But the earlier you move
      to mitigate the challenges, the sooner you can capitalise on the sizeable
      commercial opportunities.
    </h2>
    <p className="article-date">23 January 2026</p>
    <p>
      For offshore wind manufacturers who export to EU customers, the new CBAM
      regulations are forcing a strategic and operational rethink. Yes, the
      legal obligation to surrender CBAM certificates rests with the EU
      customer. But this economic burden will inevitably be pushed
      upstream.{" "}
    </p>
    <p>
      Manufacturers with higher-emission production will effectively introduce
      greater financial liabilities to their products, directly eroding their
      price competitiveness<sup>[1]</sup>. Our in-house analysis suggests that,
      depending on the specific foundation type and if no mitigation actions are
      taken, CBAM regulations can translate to a cost increase of 13-27% on top
      of standard fabrication costs, significantly threatening profit margins.
      This dynamic creates a significant commercial risk for carbon-intensive
      suppliers, who may face a loss of EU market share as the cost of their
      embedded emissions rises.
    </p>
    <p>
      Consequently, supply chains are being reshaped by carbon intensity. EU
      importers are likely to favour manufacturers that can prove lower
      production emissions to minimise their own certificate surrender
      obligations. This article is a summary of the strategic and operational
      levers you can pull to reduce your emissions as an offshore wind
      manufacturer. And the sooner you start making changes, the easier it will
      be to capitalise on the commercial opportunities.
    </p>
    <p>We will cover: </p>
    <ul className="list-disc list-inside">
      <li>
        CBAM compliant Measurement, Reporting and Verification (MRV)
        systems{" "}
      </li>
      <li>Carbon price credit management </li>
      <li>CBAM governance and performance management </li>
      <li>Supplier emissions data transparency </li>
      <li>Low carbon steel sourcing </li>
      <li>Long-term agreements for low carbon steel </li>
      <li>Supplier decarbonisation partnerships </li>
      <li>Material yield optimisation </li>
      <li>Equipment electrification </li>
      <li>Energy efficiency program </li>
      <li>Reusable energy integration</li>
    </ul>
    <h3>CBAM governance and data</h3>
    <br />
    <h4 className="text-4xl font-bold">CBAM compliant MRV system</h4>
    <p>
      <b>The actions:</b>
    </p>
    <p>
      To avoid losing orders due to data gaps, non-EU manufacturers must
      implement credible MRV systems at the product level. The system should
      track on-site fuel and electricity consumption and rigorously allocate
      these emissions to specific products (e.g., monopiles, jackets) –
      collecting product-specific data for steel and other key inputs in line
      with CBAM methodologies. Long-term offtake agreements will be increasingly
      tied to verified decarbonisation performance.
    </p>
    <p>
      <b>The benefits:</b>
    </p>
    <p>
      Under the definitive CBAM regime, if actual emissions cannot be adequately
      determined, the declared emissions will be calculated using "default
      values" based on the average emission intensity of the worst-performing
      installations in the EU<sup>[3]</sup>. Because these defaults are designed
      to be punitive, establishing robust evidence of actual emissions results
      in a lower declared footprint and prevents artificially inflated costs.
      This transparency also provides internal visibility on where emissions
      arise, highlighting the most effective decarbonisation levers.
    </p>
    <p>
      <b>The challenges:</b>
    </p>
    <p>
      Implementing a sufficient MRV system is resource-intensive. It requires
      upgrading metering and data infrastructure, defining strong allocation
      rules for shared processes (e.g. welding and coating) and securing
      recurring engagement with accredited verifiers. Furthermore, clear
      ownership must be established across the company to ensure MRV does not
      devolve into a "box-ticking" exercise that fails to withstand external
      audit.
    </p>
    <br />
    <h4 className="text-4xl font-bold">Carbon price credit management</h4>
    <p>
      <b>The actions:</b>
    </p>
    <p>
      Under Article 9 of the CBAM Regulation, the EU importer may claim a
      reduction in the number of CBAM certificates they must surrender
      corresponding to the carbon price already paid in the country of origin
      <sup>[3]</sup>. Offshore wind manufacturers with EU customers should
      establish a standardised process in procurement and finance to identify
      where mandatory domestic carbon pricing schemes (i.e. taxes or ETS) apply
      to production. Collect rigorous evidence of the carbon price effectively
      paid and allocate these to specific products or shipments, then provide
      the necessary certified documentation to EU customers.
    </p>
    <p>
      <b>The benefits:</b>
    </p>
    <p>
      Precise documentation and allocation can materially reduce net CBAM
      exposure and demonstrate transparently where the manufacturers already
      bear carbon costs. Note that this deduction applies strictly to mandatory
      carbon pricing instruments; voluntary carbon offsets or credits are
      currently not eligible<sup>[3]</sup>.
    </p>
    <p>
      <b>The challenges:</b>
    </p>
    <p>
      The documentation burden is high and deduction is limited to the
      "effective" price paid. This means any rebates, exemptions or free
      allocations the manufacturer received in the country of origin must be
      subtracted from the deduction claim<sup>[3]</sup>. Consequently,
      allocation methodologies must align perfectly with CBAM logic to avoid
      these deductions being challenged or rejected by the European Commission.
      It’s worth noting that carbon price in major export countries like China
      may be significantly lower than that in EU, limiting the effectiveness of
      the carbon credit claim. In time, policy harmonisation between the EU and
      its major trading partners – such as China – may eventually reduce this
      reporting burden.
    </p>
    <br />
    <h4 className="text-4xl font-bold">
      CBAM governance and performance management
    </h4>
    <p>
      <b>The actions:</b>
    </p>
    <p>
      CBAM outcomes are the aggregate of many local decisions. Establish a
      cross-functional task force comprising Procurement, Finance and Technical
      teams that embeds specific CBAM-relevant Key Performance Indicators (KPIs)
      (e.g. carbon intensity of finished products, share of low-carbon steel and
      supplier data coverage) in management scorecards.{" "}
    </p>
    <p>
      <b>The benefits:</b>
    </p>
    <p>
      Clear governance ensures that procurement teams do not source cheaper,
      high-carbon steel that ultimately becomes more expensive due to the CBAM
      levy. Assigning clear accountability ensures carbon costs are internalised
      in routine sourcing decisions.
    </p>
    <p>
      <b>The challenges:</b>
    </p>
    <p>
      Poorly chosen or unrealistic KPIs are easily ignored. Leadership attention
      may wane and weak underlying data can undermine trust in CBAM-related
      metrics.
    </p>
    <br />
    <h4 className="text-4xl font-bold">Supplier emissions data transparency</h4>
    <p>
      <b>The actions:</b>
    </p>
    <p>
      CBAM calculations are granular. Under the Implementing Regulation, EU
      importers are legally required to report specific parameters regarding the
      production technology and direct/indirect emissions of precursors
      <sup>[5]</sup>. Include in contracts and Requests for Quotations (RFQs)
      that key suppliers, especially steel mills, must provide product-specific
      emissions data using the standardised CBAM Communication Template
      published by the European Commission as explained in the Guidance Document
      on CBAM Implementation for Installation Operators outside the EU{" "}
      <sup>[4]</sup>. Contracts must stipulate that data includes not just total
      emissions, but specific precursor data (e.g. ferro-alloys, pig iron) and
      production route details (e.g. blast furnace vs electric arc furnace),
      supported by documentation on system boundaries and allocation rules.
    </p>
    <p>
      <b>The benefits:</b>
    </p>
    <p>
      Securing this level of detail allows manufacturers to capture the benefit
      of low-carbon steel in their final calculations. Furthermore, mandating
      the Communication Template standardises data flow, allowing manufacturers
      to differentiate mills on actual carbon performance rather than marketing
      claims.
    </p>
    <p>
      <b>The challenges:</b>
    </p>
    <p>
      Many non-EU suppliers lack the internal monitoring to provide
      “installation-specific” data, often relying on generic factors which are
      inadmissible for CBAM. Suppliers may view these requests as commercially
      sensitive. Internal teams must therefore possess the capability to
      validate incoming templates to ensure they are complete and technically
      consistent before passing them to EU clients.
    </p>
    <h3>Steel decarbonisation and supply chain optimisation</h3>
    <p>
      To prioritise mitigation efforts effectively, manufacturers must
      distinguish between raw material extraction (Stage A1) and fabrication
      (Stage A3) under BS EN 15978. Steel Construction Institute (SCI) analysis
      indicates S355 steel plate emissions at Stage A1 are significantly higher
      than Stage A3 emissions (2.6 vs 0.247 kgCO2e/kg steel)<sup>[13]</sup>.
      With A3 emissions representing only ~9.5% of the A1 emissions, the vast
      majority of embedded emissions are inherited from the mill. While actual
      A3 emissions will vary slightly depending on the specific product type
      (e.g. monopile vs jacket) and the operational efficiency of the
      fabrication process, the disparity remains significant. Consequently, the
      most impactful CBAM mitigation strategy lies in the upstream steel
      decarbonisation rather than internal operational efficiency.
    </p>
    <br />
    <h4 className="text-4xl font-bold">Low carbon steel sourcing</h4>
    <p>
      <b>The actions:</b>
    </p>
    <p>
      Steel production typically accounts for approximately 50% of the total
      lifecycle emissions of an offshore wind farm, with the majority
      concentrated in the foundations<sup>[6]</sup>. Manufacturers should adjust
      procurement criteria to favour steel produced via the electric arc furnace
      (EAF) route using high scrap content, or emerging low-carbon routes such
      as hydrogen-direct reduced iron (H2-DRI). This sourcing strategy must
      specifically target the steel plate grades required for offshore
      foundations (e.g. S355, S420, S460) and mandate verified product-level
      footprints rather than generic mill averages.
    </p>
    <p>
      <b>The benefits:</b>
    </p>
    <p>
      Switching from the conventional blast furnace-basic oxygen furnace
      (BF-BOF) route to scrap-based EAF can reduce emissions intensity from
      approximately 2.3 tonnes of CO2 per tonne of steel to roughly 0.7 tonnes
      <sup>[7]</sup>. This 70% reduction lowers the required quantity of CBAM
      certificates in the same proportion, effectively acting as a hedge against
      rising European carbon prices.
    </p>
    <p>
      <b>The challenges:</b>
    </p>
    <p>
      Availability is the primary constraint here. Low-carbon steel plate in
      offshore grades is scarce and carries a premium. Furthermore, the CBAM
      financial benefit can only be realised if the mill provides actual data
      verified according to EU standards. Relying on default values for EAF
      steel would negate the benefit.
    </p>
    <br />
    <h4 className="text-4xl font-bold">
      Long-term agreements (LTAs) for low carbon steel
    </h4>
    <p>
      <b>The actions:</b>
    </p>
    <p>
      As explained above, high-grade low-emission steel plate is scarce, with
      demand from automotive and construction sectors outstripping supply
      <sup>[8]</sup>. Enter into multi-year offtake agreements with selected
      steel mills for guaranteed volumes of certified low-carbon steel (e.g.
      H2-DRI or scrap-based EAF). These contracts must include specific clauses
      defining emissions performance thresholds, mandatory data provision via
      Environmental Product Declarations (EPDs) and "step-in" rights if
      decarbonisation milestones are missed.
    </p>
    <p>
      <b>The benefits:</b>
    </p>
    <p>
      LTAs secure manufacturers’ access to critical volume while providing mills
      with the demand signal necessary to unlock billions in capex for new green
      hydrogen or electric furnace technologies. (The long-term offtake
      agreement between Ørsted and Dillinger is a good example<sup>[6]</sup>.)
      Financially an LTA replaces the volatile cost of future CBAM certificates
      and ETS levies with a fixed, predictable "green premium," effectively
      acting as a hedge against regulatory price shocks.
    </p>
    <p>
      <b>The challenges:</b>
    </p>
    <p>
      Mills typically offer favourable LTAs only to "tier-one" customers with
      strong credit ratings and stable, large-volume demand. Furthermore
      multi-year commitments expose manufacturers to technology risk; if a
      mill’s decarbonisation project (e.g. a hydrogen electrolyser) is delayed,
      the manufacturer may be left paying a premium for steel that fails to meet
      the emissions threshold required to lower the CBAM liability.
    </p>
    <br />
    <h4 className="text-4xl font-bold">
      Supplier decarbonisation partnerships
    </h4>
    <p>
      <b>The actions:</b>
    </p>
    <p>
      Most emissions in an offshore foundation's lifecycle sit upstream in
      steelmaking. Manufacturers should shift from transactional sourcing to
      strategic collaboration with key steel and component suppliers. This
      involves co-developing decarbonisation roadmaps, supporting pilot projects
      (e.g. testing green hydrogen steel batches) and linking commercial terms
      to the provision of verified actual data rather than defaults. See, for
      instance, Vestas partnering directly with ArcelorMittal to secure
      low-emission steel plate capacity for the Baltic Power Project
      <sup>[9]</sup>.
    </p>
    <p>
      <b>The benefits:</b>
    </p>
    <p>
      Partnering with suppliers accelerates their transition by providing the
      demand signal they need to invest in new technologies like EAF or hydrogen
      reduction. For manufacturers this secures early access to scarce
      lower-carbon volumes and ensures their supply chain is "CBAM-ready",
      equipped with the necessary data granularity before competitors adapt.
    </p>
    <p>
      <b>The challenges:</b>
    </p>
    <p>
      Leverage is a key constraint. If a manufacturer’s volume represents only a
      small share of a steel mill's output, the manufacturer’s ability to
      influence the mill’s capex decisions is limited<sup>[10]</sup>.
      Furthermore these partnerships often require "co-investment" or volume
      commitments at premium prices ("green premiums") before the technology is
      fully proven, carrying the risk that pilot batches may initially
      underperform on technical specifications or yield<sup>[11]</sup>.
    </p>
    <h3>Operation decarbonisation</h3>
    <br />
    <h4 className="text-4xl font-bold">Material yield optimisation</h4>
    <p>
      <b>The actions:</b>
    </p>
    <p>
      Under the CBAM methodology for complex goods (Regulation 2023/956 Annex
      IV), embedded emissions are calculated based on the total mass of
      precursors consumed in the process, not just the mass sitting in the final
      product<sup>[3]</sup>. Manufacturers should reduce the mass ratio (tonnes
      of steel plates purchased per tonne of finished foundation) through
      advanced nesting software, cutting optimisation and ordering plate
      dimensions that match design specifications more closely. This approach
      minimises the generation of pre-consumer scrap during fabrication.
    </p>
    <p>
      <b>The benefits:</b>
    </p>
    <p>
      Any steel that ends up as scrap effectively attaches its embedded
      emissions to the finished monopile or jacket, inflating its carbon
      intensity. Therefore a 5–10% improvement in material yield translates
      directly into a 5–10% reduction in CBAM certificate liability, delivering
      a "double dividend" of lower material spend and lower carbon tax exposure.
    </p>
    <p>
      <b>The challenges:</b>
    </p>
    <p>
      Implementation must avoid disrupting throughput or quality. Consistent
      tracking of input and output mass by product is required to sustain
      improvements and evidence them in CBAM calculations.
    </p>
    <br />
    <h4 className="text-4xl font-bold">Equipment electrification</h4>
    <p>
      <b>The actions:</b>
    </p>
    <p>
      For the iron and steel sector, the initial CBAM financial liability
      applies strictly to Direct Emissions (Scope 1) from fuel combustion but
      not Indirect Emissions (Scope 2)<sup>[3]</sup>. Manufacturers should phase
      in electric forklifts, trucks and cranes, and replace diesel generators
      with grid connections as existing equipment reaches end of life. This
      strategy should be coordinated with the availability of on-site charging
      infrastructure and, where possible, linked to low-carbon electricity
      procurement.
    </p>
    <p>
      <b>The benefits:</b>
    </p>
    <p>
      Replacing diesel-burning equipment with electric alternatives effectively
      shifts energy consumption from Scope 1 to Scope 2, thereby directly
      reducing the required number of CBAM certificates. Beyond the regulatory
      arbitrage, electric forklift trucks typically offer lower opex and zero
      tailpipe emissions<sup>[12]</sup>, improving local air quality and
      occupational health in enclosed fabrication halls.
    </p>
    <p>
      <b>The challenges:</b>
    </p>
    <p>
      The primary risk is the regulatory inclusion of Scope 2 emissions. If the
      financial scope is extended to cover indirect emissions, the "regulatory
      arbitrage" of electrification evaporates. For manufacturers operating on
      carbon-intensive grids (e.g. coal-heavy networks), this could backfire:
      the CBAM liability for the grid electricity consumed may be similar or
      even exceed the liability of the original diesel fuel. Consequently the
      capex spent on electrification would yield no reduction in CBAM costs,
      effectively becoming a stranded investment unless coupled with a green
      power supply.
    </p>
    <br />
    <h4 className="text-4xl font-bold">Energy efficiency program</h4>
    <p>
      <b>The actions:</b>
    </p>
    <p>
      Optimising thermal processes (e.g. preheating and flame-cutting) directly
      reduces Direct Emissions. Manufacturers should establish a structured
      energy-efficiency programme combining regular audits and efficiency
      upgrades (such as plate rolling and welding processes) tracked against
      energy per tonne of output.
    </p>
    <p>
      <b>The benefits:</b>
    </p>
    <p>
      Reducing electricity consumption prepares the yard for the possible
      extension of CBAM liability to Indirect Emissions (Scope 2), while
      delivering immediate opex savings on utility bills.
    </p>
    <p>
      <b>The challenges:</b>
    </p>
    <p>
      Electricity savings do not currently reduce the importer's certificate
      surrender obligation for steel, making it harder to justify the capex
      required for establishing a large-scale programme.
    </p>
    <br />
    <h4 className="text-4xl font-bold">Renewable energy integration</h4>
    <p>
      <b>The actions:</b>
    </p>
    <p>
      As above, it’s possible that CBAM liability will be extended to include
      Scope 2 emissions. Therefore manufacturers should deploy on-site solar
      where feasible and procure certified green electricity via Power Purchase
      Agreements (PPAs).
    </p>
    <p>
      <b>The benefits:</b>
    </p>
    <p>
      Establishing renewable supply hedges against the potential expansion of
      CBAM’s scope, helping to negate the risk of increased certificate
      costs.{" "}
    </p>
    <p>
      <b>The challenges:</b>
    </p>
    <p>
      The risk of stranded value is significant. Because it’s still uncertain
      when Scope 2 emissions will be integrated into CBAM, paying a "green
      premium" for a PPA today does not guarantee the reduction of the EU
      importers’ CBAM liability.
    </p>
    <h3>Key takeaways</h3>
    <br />
    <h4 className="text-4xl font-bold">
      Decarbonisation is a commercial imperative, not just a policy goal
    </h4>
    <p>
      Investment in cleaner energy inputs, process upgrades and efficiency,
      underpinned by verified MRV systems, is no longer optional for offshore
      wind manufacturers selling into Europe. In fact it’s the primary lever to
      drive down CBAM costs and secure price competitiveness in the EU market.
    </p>
    <br />
    <h4 className="text-4xl font-bold">
      Early movers will capture the green advantage
    </h4>
    <p>
      As supply chains are screened for carbon intensity, transparent and
      independently verified low-carbon products will increasingly become the
      customer preference. Manufacturers who act now can win market share before
      other non-EU regions adopt similar carbon border regimes.
    </p>
    <br />
    <h4 className="text-4xl font-bold">
      Tendering strategies must internalise CBAM from day one
    </h4>
    <p>
      Project delivery teams must adapt immediately. Expect upcoming RFQs to
      require granular emissions datasets and to explicitly favour manufacturers
      that de-risk the project via cleaner inputs, data readiness and robust
      long-term improvement plans.
    </p>
    <h3>You may also be interested in:</h3>
    <p>
      &gt;&gt;{" "}
      <a
        href="https://www.windenergybridge.com/insights/cbam-for-offshore-wind:-an-introduction-to-customs-and-border-processes"
        target="_blank"
        rel="noreferrer noopener"
      >
        CBAM for offshore wind: an introduction to customs and border processes
      </a>
    </p>
    <p>
      &gt;&gt;{" "}
      <a
        href="https://www.windenergybridge.com/insights/cbam:-market-implications-for-the-offshore-wind-sector"
        target="_blank"
        rel="noreferrer noopener"
      >
        CBAM: regulations and compliance for the offshore wind sector
      </a>
    </p>
    <p>
      &gt;&gt;{" "}
      <a
        href="https://www.windenergybridge.com/insights/cbam:-market-implications-for-the-offshore-wind-sector"
        target="_blank"
        rel="noreferrer noopener"
      >
        CBAM: market implications for the offshore wind sector
      </a>
    </p>
    <h3>References</h3>
    <ul className="list-disc list-inside">
      <li>
        [1] PwC (2024): "The EU Carbon Border Adjustment Mechanism (CBAM):
        Implications for supply chains." Available at:{" "}
        <a
          href="https://www.pwc.com/gx/en/services/tax/esg-tax/cbam-supply-chain-imperatives.html"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://www.pwc.com/gx/en/services/tax/esg-tax/cbam-supply-chain-imperatives.html
        </a>{" "}
      </li>
      <li>
        [2] WindEnergyBridge (2024): "CBAM: regulations and compliance for the
        offshore wind sector." Available at:{" "}
        <a
          href="https://www.windenergybridge.com/insights/cbam:-regulations-and-compliance-for-the-offshore-wind-sector"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://www.windenergybridge.com/insights/cbam:-regulations-and-compliance-for-the-offshore-wind-sector
        </a>{" "}
      </li>
      <li>
        [3] Regulation (EU) 2023/956: "Regulation of the European Parliament and
        of the Council establishing a carbon border adjustment mechanism."
        Available at:{" "}
        <a
          href="https://eur-lex.europa.eu/eli/reg/2023/956/oj/eng"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://eur-lex.europa.eu/eli/reg/2023/956/oj/eng
        </a>{" "}
      </li>
      <li>
        [4] European Commission: "CBAM Legislation and Guidance." Available at:{" "}
        <a
          href="https://taxation-customs.ec.europa.eu/carbon-border-adjustment-mechanism/cbam-legislation-and-guidance_en"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://taxation-customs.ec.europa.eu/carbon-border-adjustment-mechanism/cbam-legislation-and-guidance_en
        </a>
      </li>
      <li>
        [5] Implementing Regulation (EU) 2023/1773: "Commission Implementing
        Regulation laying down the rules for the application of Regulation (EU)
        2023/956 of the European Parliament and of the Council as regards
        reporting obligations for the purposes of the carbon border adjustment
        mechanism during the transitional period." Available at:{" "}
        <a
          href="https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=OJ%3AJOL_2023_228_R_0006"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=OJ%3AJOL_2023_228_R_0006
        </a>{" "}
      </li>
      <li>
        [6] Ørsted (2024): "Laying the foundations for low-emission steel."
        Available at:{" "}
        <a
          href="https://orsted.com/en/what-we-do/insights/expert-take/laying-the-foundations-for-low-emission-steel"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://orsted.com/en/what-we-do/insights/expert-take/laying-the-foundations-for-low-emission-steel
        </a>{" "}
      </li>
      <li>
        [7] European Commission, Joint Research Centre (JRC) (2025): " Defining
        low-carbon emissions steel: A comparative analysis of international
        initiatives and standards." Available at:{" "}
        <a
          href="https://publications.jrc.ec.europa.eu/repository/bitstream/JRC141817/JRC141817_01.pdf"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://publications.jrc.ec.europa.eu/repository/bitstream/JRC141817/JRC141817_01.pdf
        </a>{" "}
      </li>
      <li>
        [8] Mordor Intelligence (2025): "Flat Carbon Steel Market Size, Share &
        2030 Growth Trends Report." Available at:{" "}
        <a
          href="https://www.mordorintelligence.com/industry-reports/flat-carbon-steel-market"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://www.mordorintelligence.com/industry-reports/flat-carbon-steel-market
        </a>{" "}
      </li>
      <li>
        [9] Vestas (2024): "Vestas introduces ArcelorMittal’s low
        carbon-emissions steel offering for wind turbines." Available at:{" "}
        <a
          href="https://corporate.arcelormittal.com/media/news-articles/vestas-introduces-arcelormittal-s-low-carbon-emissions-steel-offering-for-wind-turbines"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://corporate.arcelormittal.com/media/news-articles/vestas-introduces-arcelormittal-s-low-carbon-emissions-steel-offering-for-wind-turbines
        </a>{" "}
      </li>
      <li>
        [10] SustainCERT (2024): "Supplier engagement for Scope 3 reductions."
        Available at:{" "}
        <a
          href="https://www.sustain-cert.com/news/how-to-create-a-supplier-engagement-strategy-for-scope-3-emission-reductions"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://www.sustain-cert.com/news/how-to-create-a-supplier-engagement-strategy-for-scope-3-emission-reductions
        </a>{" "}
      </li>
      <li>
        [11] ODI (2024): "Steel in transition: aligning climate, trade and
        industrial policy." Available at:{" "}
        <a
          href="https://media.odi.org/documents/Steel_Briefing_Paper.pdf"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://media.odi.org/documents/Steel_Briefing_Paper.pdf
        </a>{" "}
      </li>
      <li>
        [12] MasterMover (2024): "Green Alternatives to Diesel Forklift Trucks."
        Available at:{" "}
        <a
          href="https://www.mastermover.com/material-handling/electric-tugs-vs-diesel-forklifts-sustainability"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://www.mastermover.com/material-handling/electric-tugs-vs-diesel-forklifts-sustainability
        </a>{" "}
      </li>
      <li>
        [13] Steel Construction Institute (SCI): "STROBE D5-1 Weight, cost and
        carbon comparisons." Available at:{" "}
        <a
          href="https://steel-sci.com/assets/downloads/reports/STROBE%20D5-1%20Weight,%20cost%20and%20carbon%20comparisons.pdf"
          target="_blank"
          rel="noreferrer noopener"
        >
          https://steel-sci.com/assets/downloads/reports/STROBE%20D5-1%20Weight,%20cost%20and%20carbon%20comparisons.pdf
        </a>{" "}
      </li>
    </ul>

    <br />
    <br />
    <br />

    <br />
    <br />
    <br />
    <p>
      As the global offshore wind sector continues to advance, innovation,
      investments, and supportive policies are driving transformative growth. At{" "}
      <i>
        <b>WeBridge</b>
      </i>
      , we provide the expertise and insights our partners need to confidently
      navigate this evolving landscape.
    </p>
    <h3 style={{ marginTop: "200px" }}>Subscribe</h3>
    <ContactSubscribe />
  </div>,
);
